import { useCallback, useEffect, useRef } from "react";
import { isFileTypeAllowed } from "../services/fileUploadService";

/**
 * Options for the useClipboardPaste hook
 */
export interface UseClipboardPasteOptions {
  /** Whether paste functionality is enabled */
  enabled?: boolean;
  /** Callback when files are pasted from clipboard */
  onFilesPasted: (files: File[]) => void;
  /** Callback when invalid file types are pasted */
  onInvalidFileType?: (files: File[]) => void;
  /** Optional ref to a specific element to listen for paste events on */
  targetRef?: React.RefObject<HTMLElement | null>;
}

/**
 * Return type for the useClipboardPaste hook
 */
export interface UseClipboardPasteReturn {
  /** Handler to attach to paste event manually if needed */
  handlePaste: (event: ClipboardEvent | React.ClipboardEvent) => void;
}

/**
 * Extract files from clipboard data
 * Handles both file types from clipboard (images, files)
 */
const extractFilesFromClipboard = (clipboardData: DataTransfer | null): File[] => {
  if (!clipboardData) return [];
  
  const files: File[] = [];
  
  // Check for files in clipboard items
  if (clipboardData.items) {
    for (let i = 0; i < clipboardData.items.length; i++) {
      const item = clipboardData.items[i];
      
      // Check if the item is a file
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) {
          files.push(file);
        }
      }
    }
  }
  
  // Fallback: check clipboardData.files directly
  if (files.length === 0 && clipboardData.files && clipboardData.files.length > 0) {
    for (let i = 0; i < clipboardData.files.length; i++) {
      const file = clipboardData.files[i];
      if (file) {
        files.push(file);
      }
    }
  }
  
  return files;
};

/**
 * Custom hook for handling clipboard paste events with file support.
 * 
 * This hook allows users to paste files directly from their clipboard
 * into the chat input. It supports:
 * - Images copied from clipboard (screenshots, copied images)
 * - Files copied from file explorer
 * 
 * @example
 * ```tsx
 * const { handlePaste } = useClipboardPaste({
 *   enabled: !isInputDisabled,
 *   onFilesPasted: (files) => handleFileSelect(files),
 *   onInvalidFileType: (files) => toast.error('Invalid file type'),
 *   targetRef: inputRef,
 * });
 * ```
 */
const useClipboardPaste = ({
  enabled = true,
  onFilesPasted,
  onInvalidFileType,
  targetRef,
}: UseClipboardPasteOptions): UseClipboardPasteReturn => {
  // Track if we've set up listeners
  const isSetupRef = useRef(false);
  
  /**
   * Main paste handler
   * Extracts files from clipboard and validates them before calling the callback
   */
  const handlePaste = useCallback(
    (event: ClipboardEvent | React.ClipboardEvent) => {
      if (!enabled) return;
      
      // Get clipboard data from the event
      const clipboardData = 
        "clipboardData" in event 
          ? event.clipboardData 
          : (event as ClipboardEvent).clipboardData;
      
      // Extract files from clipboard
      const files = extractFilesFromClipboard(clipboardData);
      
      // If no files found, let the default paste behavior continue (for text)
      if (files.length === 0) return;
      
      // Prevent default paste behavior since we're handling files
      event.preventDefault();
      
      // Separate valid and invalid files based on allowed file types
      const validFiles: File[] = [];
      const invalidFiles: File[] = [];
      
      files.forEach((file) => {
        if (isFileTypeAllowed(file)) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file);
        }
      });
      
      // Handle invalid files
      if (invalidFiles.length > 0 && onInvalidFileType) {
        onInvalidFileType(invalidFiles);
      }
      
      // Handle valid files
      if (validFiles.length > 0) {
        onFilesPasted(validFiles);
      }
    },
    [enabled, onFilesPasted, onInvalidFileType]
  );
  
  // Set up paste event listener on the target element or document
  useEffect(() => {
    if (!enabled) return;
    
    const target = targetRef?.current || document;
    
    const pasteHandler = (event: Event) => {
      handlePaste(event as ClipboardEvent);
    };
    
    // Only add listener if not already set up
    if (!isSetupRef.current) {
      target.addEventListener("paste", pasteHandler);
      isSetupRef.current = true;
    }
    
    return () => {
      target.removeEventListener("paste", pasteHandler);
      isSetupRef.current = false;
    };
  }, [enabled, handlePaste, targetRef]);
  
  return {
    handlePaste,
  };
};

export default useClipboardPaste;

